import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import {
  buildBreadcrumbJsonLd,
  buildCanonicalUrl,
  buildWebPageJsonLd,
  organizationJsonLd,
  websiteJsonLd,
} from "@/lib/seo";
import {
  Award,
  Users,
  Globe,
  TrendingUp,
  CheckCircle,
  Target,
} from "lucide-react";
const vishnuPhoto = "/team/vishnu-chit-sharma.png";
const ranjeetPhoto = "/team/ranjeet-ranjan-mishra.png";

export default function AboutPage() {
  const stats = [
    { icon: Users, value: "10K+", label: "Satisfied Customers", color: "blue" },
    { icon: Globe, value: "50+", label: "Global Destinations", color: "green" },
    {
      icon: TrendingUp,
      value: "25+",
      label: "Currencies Supported",
      color: "purple",
    },
    {
      icon: Award,
      value: "15+",
      label: "Years of Excellence",
      color: "orange",
    },
  ];

  const features = [
    "RBI Approved AD II Category License for Forex Operations",
    "IATA, TAAI, and IATO certified travel services",
    "Transparent pricing with competitive exchange rates",
    "Comprehensive travel packages and visa assistance",
    "Expert guidance for international money transfers",
    "Trusted partner for corporate and individual clients",
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case "blue":
        return "bg-blue-100 text-blue-600";
      case "green":
        return "bg-green-100 text-green-600";
      case "purple":
        return "bg-purple-100 text-purple-600";
      case "orange":
        return "bg-orange-100 text-orange-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="About Travel Wings Global | RBI Approved Forex & Travel Experts"
        description="Learn about Travel Wings Global's RBI-approved forex services, IATA/TAAI certified travel solutions, and 15+ years of trusted experience."
        path="/about"
        structuredData={[
          organizationJsonLd,
          websiteJsonLd,
          buildWebPageJsonLd({
            name: "About Travel Wings Global | RBI Approved Forex & Travel Experts",
            description:
              "Travel Wings Global offers forex, curated travel, and visa services with 15+ years of expertise.",
            url: buildCanonicalUrl("/about"),
            type: "AboutPage",
          }),
          buildBreadcrumbJsonLd([
            { name: "Home", url: buildCanonicalUrl("/") },
            { name: "About", url: buildCanonicalUrl("/about") },
          ]),
        ]}
      />
      <Navigation />

      {/* Hero Section */}
      <section className="pt-16 pb-16 bg-gradient-to-b from-tw-orange-light to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            About <span className="text-tw-orange">Travel Wings</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Your trusted partner for seamless international experiences,
            combining competitive currency exchange rates with meticulously
            curated travel packages.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-tw-orange-light text-tw-orange rounded-full text-sm font-medium mb-4">
              <Target className="w-4 h-4 mr-2" />
              About Travel Wings
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Established <span className="text-tw-orange">2010</span> •{" "}
              <span className="text-tw-purple">15+ Years</span> of Excellence
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Travel Wings Forex Private Limited (formerly Make My Travel India
              Pvt. Ltd.) is a premier destination for comprehensive travel and
              forex services. As an RBI-approved AD II Category license holder,
              we provide trusted financial solutions alongside expertly curated
              travel experiences.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Who We Are
              </h3>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Founded in 2010, Travel Wings has evolved from Make My Travel
                India Pvt. Ltd. into a leading provider of integrated travel and
                forex services. Our RBI-approved AD II Category license ensures
                that all foreign exchange operations meet the highest regulatory
                standards.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We are proud members of prestigious industry associations
                including IATA (International Air Transport Association), TAAI
                (Travel Agents Association of India), and IATO (Indian
                Association of Tour Operators), reflecting our commitment to
                excellence and industry best practices.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our mission is to simplify international travel through
                transparent forex services and comprehensive travel solutions,
                making global experiences accessible to individuals and
                businesses alike.
              </p>

              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-6">
                <img
                  src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600"
                  alt="Modern office workspace"
                  className="rounded-2xl shadow-lg w-full h-64 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1559526324-4b87b5e36e44?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600"
                  alt="Currency exchange"
                  className="rounded-2xl shadow-lg w-full h-64 object-cover mt-8"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-1">
                    99.9%
                  </div>
                  <div className="text-sm text-gray-600">
                    Customer Satisfaction
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              const colorClasses = getColorClasses(stat.color);
              return (
                <div key={index} className="text-center">
                  <div
                    className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 ${colorClasses}`}
                  >
                    <IconComponent className="w-8 h-8" />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              );
            })}
          </div>

          {/* Management Section */}
          <div className="bg-gradient-to-b from-gray-50 to-white rounded-3xl p-8 md:p-12">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Our <span className="text-tw-orange">Management</span>
              </h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Meet the experienced leaders who drive Travel Wings' vision of
                excellence in travel and forex services.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
              {/* Vishnu Chit Sharma */}
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow">
                <div className="w-32 h-32 rounded-full mx-auto mb-6 overflow-hidden">
                  <img
                    src={vishnuPhoto}
                    alt="Vishnu Chit Sharma - Managing Director"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-2">
                  Vishnu Chit Sharma
                </h4>
                <p className="text-tw-orange font-medium mb-4">
                  Managing Director
                </p>
                <p className="text-gray-600 leading-relaxed">
                  An accomplished leader with over 30+ years of expertise in
                  luxury and corporate travel management, delivering exceptional
                  experiences for discerning clients. His deep understanding of
                  market dynamics, superior operational, and profitability in a
                  highly competitive market. Expert in crafting tailored travel
                  solutions, financing, supervision and team leadership, he has
                  built Travel Wings into a customer-focused organization
                  experiencing business growth in the competitive travel
                  industry.
                </p>
              </div>

              {/* Ranjeet Ranjan Mishra */}
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow">
                <div className="w-32 h-32 rounded-full mx-auto mb-6 overflow-hidden">
                  <img
                    src={ranjeetPhoto}
                    alt="Ranjeet Ranjan Mishra - Director"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-2">
                  Ranjeet Ranjan Mishra
                </h4>
                <p className="text-tw-purple font-medium mb-4">Director</p>
                <p className="text-gray-600 leading-relaxed">
                  An experienced and versatile leader in the travel industry
                  with over 29+ years of expertise in foreign exchange and
                  marketing. Known for designing financial solutions with
                  innovative marketing strategies to help clients achieve their
                  travel goals efficiently. His strategic approach in developing
                  specialized marketing strategies and enhanced brand visibility
                  makes a distinct revenue and client business growth in
                  competitive travel and tourism industry, delivering
                  exceptional results in competitive travel and tourism markets.
                </p>
              </div>
            </div>
          </div>

          {/* Associations & Recognitions */}
          <div className="bg-gradient-to-b from-tw-orange-light to-white rounded-3xl p-8 md:p-12 mt-20">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Associations &{" "}
                <span className="text-tw-orange">Recognitions</span>
              </h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Officially recognized and certified by leading industry
                associations and government bodies.
              </p>
            </div>

            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
                {/* IATA */}
                <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col items-center group cursor-pointer h-44">
                  <div className="flex-1 flex items-center justify-center w-full mb-4">
                    <img
                      src="/associations/iata-logo.png"
                      alt="IATA - International Air Transport Association"
                      className="h-16 w-auto object-contain group-hover:scale-105 transition-transform"
                      style={{ maxHeight: "80px", maxWidth: "120px" }}
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.style.display = "none";
                        const fallback = document.createElement("div");
                        fallback.className =
                          "w-20 h-16 bg-blue-600 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform";
                        fallback.innerHTML =
                          '<span class="text-white font-bold text-sm">IATA</span>';
                        target.parentNode?.insertBefore(fallback, target);
                      }}
                    />
                  </div>
                  <div className="text-sm text-center text-gray-800 font-semibold">
                    IATA Certified
                  </div>
                </div>

                {/* TAFI */}
                <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col items-center group cursor-pointer h-44">
                  <div className="flex-1 flex items-center justify-center w-full mb-4">
                    <img
                      src="/associations/tafi-logo.png"
                      alt="TAFI - Travel Agents Federation of India"
                      className="h-16 w-auto object-contain group-hover:scale-105 transition-transform"
                      style={{ maxHeight: "80px", maxWidth: "120px" }}
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.style.display = "none";
                        const fallback = document.createElement("div");
                        fallback.className =
                          "w-20 h-16 bg-blue-500 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform";
                        fallback.innerHTML =
                          '<span class="text-white font-bold text-sm">TAFI</span>';
                        target.parentNode?.insertBefore(fallback, target);
                      }}
                    />
                  </div>
                  <div className="text-sm text-center text-gray-800 font-semibold">
                    TAFI Member
                  </div>
                </div>

                {/* TAAI */}
                <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col items-center group cursor-pointer h-44">
                  <div className="flex-1 flex items-center justify-center w-full mb-4">
                    <img
                      src="/associations/taai-logo.png"
                      alt="TAAI - Travel Agents Association of India"
                      className="h-16 w-auto object-contain group-hover:scale-105 transition-transform brightness-150 contrast-150 saturate-150"
                      style={{
                        maxHeight: "80px",
                        maxWidth: "120px",
                        filter: "brightness(1.5) contrast(1.5) saturate(1.5)",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        borderRadius: "4px",
                        padding: "2px",
                      }}
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.style.display = "none";
                        const fallback = document.createElement("div");
                        fallback.className =
                          "w-20 h-16 bg-red-600 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform";
                        fallback.innerHTML =
                          '<span class="text-white font-bold text-sm">TAAI</span>';
                        target.parentNode?.insertBefore(fallback, target);
                      }}
                    />
                  </div>
                  <div className="text-sm text-center text-gray-800 font-semibold">
                    TAAI Member
                  </div>
                </div>

                {/* Ministry of Tourism */}
                <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col items-center group cursor-pointer h-44">
                  <div className="flex-1 flex items-center justify-center w-full mb-4">
                    <img
                      src="/associations/ministry-tourism-logo.png"
                      alt="Ministry of Tourism - Government of India"
                      className="h-16 w-auto object-contain group-hover:scale-105 transition-transform brightness-110 contrast-125"
                      style={{ maxHeight: "80px", maxWidth: "120px" }}
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.style.display = "none";
                        const fallback = document.createElement("div");
                        fallback.className =
                          "w-20 h-16 bg-orange-500 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform";
                        fallback.innerHTML =
                          '<span class="text-white font-bold text-sm">MOT</span>';
                        target.parentNode?.insertBefore(fallback, target);
                      }}
                    />
                  </div>
                  <div className="text-sm text-center text-gray-800 font-semibold">
                    MOT Approved
                  </div>
                </div>

                {/* IATO */}
                <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col items-center group cursor-pointer h-44">
                  <div className="flex-1 flex items-center justify-center w-full mb-4">
                    <img
                      src="/associations/iato-logo.png"
                      alt="IATO - Indian Association of Tour Operators"
                      className="h-16 w-auto object-contain group-hover:scale-105 transition-transform brightness-110 contrast-125"
                      style={{ maxHeight: "80px", maxWidth: "120px" }}
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.style.display = "none";
                        const fallback = document.createElement("div");
                        fallback.className =
                          "w-20 h-16 bg-blue-500 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform";
                        fallback.innerHTML =
                          '<span class="text-white font-bold text-sm">IATO</span>';
                        target.parentNode?.insertBefore(fallback, target);
                      }}
                    />
                  </div>
                  <div className="text-sm text-center text-gray-800 font-semibold">
                    IATO Member
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
